import React, { useState, useRef } from 'react';
import { backgroundStyleFor } from '../../Portal/backgroundLayers';

const clampPct = (n) => Math.min(100, Math.max(0, Math.round(n)));

/**
 * A small frame showing how a picture sits, with a dot you drag with the
 * mouse: the dot's position IS the picture's alignment (x%, y%) — where its
 * "focus" is when it is cropped to fit. Used for a Background layer and for an
 * Image / Logo. The frame repaints live while dragging; the change is committed
 * once on release, so one drag = one undo step.
 *
 * `size` is how the picture fills the frame ('cover' | 'contain' | 'auto'),
 * matching the CSS it will get on the page.
 */
export default function FocalPad({ src, x, y, size = 'cover', repeat = false, onCommit }) {
    const padRef = useRef(null);
    const [live, setLive] = useState(null);
    const currentX = live ? live.x : x;
    const currentY = live ? live.y : y;

    function pointFrom(e) {
        const rect = padRef.current.getBoundingClientRect();
        return {
            x: clampPct(((e.clientX - rect.left) / (rect.width || 1)) * 100),
            y: clampPct(((e.clientY - rect.top) / (rect.height || 1)) * 100),
        };
    }

    function handlePointerDown(e) {
        e.preventDefault();
        let latest = pointFrom(e);
        setLive(latest);
        const onMove = (ev) => { latest = pointFrom(ev); setLive(latest); };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            setLive(null);
            onCommit(latest.x, latest.y);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }

    return (
        <div
            ref={padRef}
            className="lb-bg-pad"
            role="group"
            aria-label="Drag to align the image"
            style={src ? backgroundStyleFor([{ src, x: currentX, y: currentY, size, repeat }]) : undefined}
            onPointerDown={handlePointerDown}
        >
            <span className="lb-bg-pad-dot" style={{ left: `${currentX}%`, top: `${currentY}%` }} />
        </div>
    );
}
